"use client";

import * as PopoverPrimitives from "@radix-ui/react-popover";

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { RiArrowDownSLine, RiCloseLine, RiSearchLine } from "react-icons/ri";

import { Checkbox } from "@/components/ui/checkbox";

import { useIsMobile } from "@/contexts/is-mobile-provider";

import { cx, focusInput } from "@/lib/tailwind-utils";

import Button from "./button";
import Input from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import Tooltip from "./tooltip";

/** Recursively extract text content from React nodes (handles JSX children). */
function extractTextContent(node: React.ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (!node) return "";
  if (Array.isArray(node)) return node.map(extractTextContent).join("");
  if (React.isValidElement(node)) {
    return extractTextContent(
      (node.props as { children?: React.ReactNode }).children,
    );
  }
  return "";
}

const displayNameOf = (type: unknown): string | undefined =>
  typeof type === "function" || typeof type === "object"
    ? (type as { displayName?: string })?.displayName
    : undefined;

const isMultiSelectItem = (
  child: React.ReactNode,
): child is React.ReactElement<MultiSelectItemProps> =>
  React.isValidElement(child) &&
  displayNameOf(child.type) === "MultiSelectItem";

const itemSearchLabel = ({ value, label, children }: MultiSelectItemProps) =>
  label || extractTextContent(children) || value;

const matchesSearch = (text: string, searchTerm: string): boolean =>
  !searchTerm || text.toLowerCase().includes(searchTerm.toLowerCase());

/** Stored answers are untyped; only a non-blank string can stand in as its own label. */
const canLabelItself = (value: unknown): value is string =>
  typeof value === "string" && !!value.trim();

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectContextType {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedValues: string[];
  toggleValue: (value: string) => void;
  /** `index` is the value's position among the selected values, when the caller knows it. */
  removeValue: (value: string, index?: number) => void;
  selectAll: () => void;
  selectNone: () => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  options: MultiSelectOption[];
  disabled?: boolean;
  hasPills?: boolean;
  maxSelections?: number;
  isAtMax: boolean;
  enableOperatorToggle: boolean;
  excludeValues: string[];
  toggleOperator: (value: string) => void;
  enableMatchMode: boolean;
  matchMode: "any" | "all";
  onMatchModeChange?: (mode: "any" | "all") => void;
  /** When true, the dropdown search does not filter options locally (use with server-backed loading). */
  remoteSearch?: boolean;
  showOffListValues: boolean;
  /** Selected values missing from the option list; empty unless showOffListValues is on. */
  offListValues: string[];
}

const MultiSelectContext = createContext<MultiSelectContextType | undefined>(
  undefined,
);

const useMultiSelect = () => {
  const context = useContext(MultiSelectContext);
  if (context === undefined) {
    throw new Error("MultiSelect components must be used within a MultiSelect");
  }
  return context;
};

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  /** When true the dropdown still opens for viewing, but selections are locked. */
  disabled?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  className?: string;
  maxSelections?: number;
  enableOperatorToggle?: boolean;
  excludeValues?: string[];
  onExcludeChange?: (values: string[]) => void;
  enableMatchMode?: boolean;
  matchMode?: "any" | "all";
  onMatchModeChange?: (mode: "any" | "all") => void;
  /** Atomic operator toggle — called instead of onChange+onExcludeChange to avoid stale closures. */
  onOperatorToggle?: (value: string) => void;
  /** Atomic remove — removes a value from whichever list (include/exclude) it belongs to. */
  onRemoveValue?: (value: string) => void;
  /** Atomic select-all — called instead of onChange+onExcludeChange to avoid stale closures. */
  onSelectAll?: () => void;
  /** Atomic select-none — called instead of onChange+onExcludeChange to avoid stale closures. */
  onSelectNone?: () => void;
  /** Fired when the user types in the dropdown search field (debounce in the parent if needed). */
  onSearchTermChange?: (term: string) => void;
  remoteSearch?: boolean;
  /**
   * Show selected values that are not in the option list, labelled with the raw
   * value, instead of hiding them. Such a value cannot be picked again once it is
   * gone, so Select All and Select None leave it alone — only its own pill removes it.
   */
  showOffListValues?: boolean;
}

const MultiSelectRoot = ({
  options,
  value,
  onChange,
  placeholder: _placeholder = "Select items...",
  searchPlaceholder: _searchPlaceholder = "Search...",
  disabled,
  hasError,
  errorMessage,
  className,
  maxSelections,
  enableOperatorToggle = false,
  excludeValues: excludeValuesProp = [],
  onExcludeChange,
  enableMatchMode = false,
  matchMode = "any",
  onMatchModeChange,
  onOperatorToggle,
  onRemoveValue: onRemoveValueProp,
  onSelectAll: onSelectAllProp,
  onSelectNone: onSelectNoneProp,
  onSearchTermChange,
  remoteSearch = false,
  showOffListValues = false,
  children,
}: MultiSelectProps & { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTermInternal] = useState("");
  const setSearchTerm = useCallback(
    (term: string) => {
      setSearchTermInternal(term);
      onSearchTermChange?.(term);
    },
    [onSearchTermChange],
  );

  const safeValue = Array.isArray(value) ? value : [];
  const safeExcludeValues = Array.isArray(excludeValuesProp)
    ? excludeValuesProp
    : [];

  const itemsFromChildren = useMemo(() => {
    const childrenArray = React.Children.toArray(children);
    const contentChild = childrenArray.find((child) => {
      if (!React.isValidElement(child)) return false;
      return displayNameOf(child.type) === "MultiSelectContent";
    });

    if (!contentChild || !React.isValidElement(contentChild)) {
      return [];
    }

    const contentChildren = React.Children.toArray(
      (contentChild.props as { children?: React.ReactNode }).children,
    );
    return contentChildren
      .filter((child) => {
        if (!React.isValidElement(child)) return false;
        return displayNameOf(child.type) === "MultiSelectItem";
      })
      .map((child) => {
        if (React.isValidElement(child)) {
          const props = child.props as {
            value: string;
            label?: string;
            children?: React.ReactNode;
          };
          const label =
            props.label || extractTextContent(props.children) || props.value;
          return {
            value: props.value,
            label,
          };
        }
        return null;
      })
      .filter((item): item is MultiSelectOption => item !== null);
  }, [children]);

  const allOptions = useMemo(() => {
    return itemsFromChildren.length > 0 ? itemsFromChildren : options;
  }, [itemsFromChildren, options]);

  const isAtMax = !!maxSelections && safeValue.length >= maxSelections;

  const isOffList = (val: string) =>
    showOffListValues &&
    canLabelItself(val) &&
    !allOptions.some((opt) => opt.value === val);
  const offListValues = safeValue.filter(isOffList);

  const toggleValue = (val: string) => {
    if (disabled) return;
    // If in exclude list, clicking checkbox moves it back to include
    if (enableOperatorToggle && safeExcludeValues.includes(val)) {
      if (onOperatorToggle) {
        onOperatorToggle(val);
      } else {
        onExcludeChange?.(safeExcludeValues.filter((v) => v !== val));
        onChange([...safeValue, val]);
      }
      return;
    }
    const isRemoving = safeValue.includes(val);
    if (!isRemoving && isAtMax) return;
    const newValues = isRemoving
      ? safeValue.filter((v) => v !== val)
      : [...safeValue, val];
    onChange(newValues);
  };

  const removeValue = (val: string, index?: number) => {
    if (disabled) return;
    if (onRemoveValueProp) {
      onRemoveValueProp(val);
      return;
    }
    // Free-text values can repeat; take out the one that was clicked, not every copy.
    if (showOffListValues && index !== undefined && safeValue[index] === val) {
      onChange(safeValue.filter((_, i) => i !== index));
      return;
    }
    if (safeExcludeValues.includes(val)) {
      onExcludeChange?.(safeExcludeValues.filter((v) => v !== val));
    } else {
      onChange(safeValue.filter((v) => v !== val));
    }
  };

  const toggleOperator = (val: string) => {
    if (disabled || !enableOperatorToggle) return;
    // Prefer atomic handler to avoid stale-closure bugs when both
    // onChange and onExcludeChange reconstruct composite state.
    if (onOperatorToggle) {
      onOperatorToggle(val);
      return;
    }
    if (safeValue.includes(val)) {
      // Move from include → exclude
      onChange(safeValue.filter((v) => v !== val));
      onExcludeChange?.([...safeExcludeValues, val]);
    } else if (safeExcludeValues.includes(val)) {
      // Move from exclude → include
      onExcludeChange?.(safeExcludeValues.filter((v) => v !== val));
      onChange([...safeValue, val]);
    }
  };

  const selectAll = () => {
    if (disabled) return;
    if (onSelectAllProp) {
      onSelectAllProp();
      return;
    }
    const allVals = allOptions.map((opt) => opt.value);
    onChange([...offListValues, ...allVals]);
    if (enableOperatorToggle && onExcludeChange) {
      onExcludeChange(safeExcludeValues.filter((v) => !allVals.includes(v)));
    }
  };

  const selectNone = () => {
    if (disabled) return;
    if (onSelectNoneProp) {
      onSelectNoneProp();
      return;
    }
    onChange(offListValues);
    if (enableOperatorToggle && onExcludeChange) {
      onExcludeChange(safeExcludeValues.filter(isOffList));
    }
  };

  // Detect if MultiSelectPills is among children
  const hasPills = useMemo(() => {
    const childrenArray = React.Children.toArray(children);
    return childrenArray.some((child) => {
      if (!React.isValidElement(child)) return false;
      return displayNameOf(child.type) === "MultiSelectPills";
    });
  }, [children]);

  return (
    <MultiSelectContext.Provider
      value={{
        open,
        setOpen,
        selectedValues: safeValue,
        toggleValue,
        removeValue,
        selectAll,
        selectNone,
        searchTerm,
        setSearchTerm,
        options: allOptions,
        disabled,
        hasPills,
        maxSelections,
        isAtMax,
        enableOperatorToggle,
        excludeValues: safeExcludeValues,
        toggleOperator,
        enableMatchMode,
        matchMode,
        onMatchModeChange,
        remoteSearch,
        showOffListValues,
        offListValues,
      }}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <div className={cx("space-y-2", className)}>
          {children}
          {hasError && errorMessage && (
            <p className="mt-1.5 text-caption text-status-error-text">
              {errorMessage}
            </p>
          )}
        </div>
      </Popover>
    </MultiSelectContext.Provider>
  );
};

MultiSelectRoot.displayName = "MultiSelectRoot";

interface MultiSelectTriggerProps extends React.ComponentPropsWithoutRef<
  typeof PopoverPrimitives.Trigger
> {
  placeholder?: string;
}

const MultiSelectTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitives.Trigger>,
  MultiSelectTriggerProps
>(
  (
    {
      className,
      placeholder = "Select items...",
      disabled: disabledProp,
      ...props
    },
    forwardedRef,
  ) => {
    const {
      open,
      selectedValues,
      excludeValues,
      options,
      disabled: contextDisabled,
      hasPills,
      enableOperatorToggle,
      showOffListValues,
    } = useMultiSelect();
    const disabled = disabledProp ?? contextDisabled;

    const selectedLabels = useMemo(() => {
      if (hasPills) return "";
      const includeLabels = selectedValues
        .map(
          (val) =>
            options.find((opt) => opt.value === val)?.label ??
            (showOffListValues && canLabelItself(val) ? val : undefined),
        )
        .filter(Boolean);
      if (!enableOperatorToggle) return includeLabels.join(", ");
      const excludeLabels = excludeValues
        .map(
          (val) => `~${options.find((opt) => opt.value === val)?.label ?? val}`,
        )
        .filter(Boolean);
      return [...includeLabels, ...excludeLabels].join(", ");
    }, [
      selectedValues,
      excludeValues,
      options,
      hasPills,
      enableOperatorToggle,
      showOffListValues,
    ]);

    return (
      <PopoverTrigger
        ref={forwardedRef}
        className={cx(
          "group/trigger flex w-full select-none items-center justify-between truncate rounded-md border px-3 py-2 shadow-sm outline-none transition sm:text-body",
          "text-foreground",
          "hover:bg-row-hover",
          // Disabled stays openable (peekable) — style as muted but keep it clickable.
          disabled && "bg-muted text-foreground-muted",
          focusInput,
          className,
        )}
        {...props}
      >
        <span className="truncate">
          {selectedLabels || (
            <span className="text-foreground-muted">{placeholder}</span>
          )}
        </span>
        <RiArrowDownSLine
          className={cx(
            "size-4 shrink-0",
            "text-foreground-muted",
            "group-data-[disabled]/trigger:text-foreground-muted/50",
            "transition-transform",
            open && "rotate-180",
          )}
        />
      </PopoverTrigger>
    );
  },
);

MultiSelectTrigger.displayName = "MultiSelectTrigger";

interface MultiSelectPanelContextType {
  allOptions: MultiSelectOption[];
  filteredOptions: MultiSelectOption[];
}

const MultiSelectPanelContext = createContext<
  MultiSelectPanelContextType | undefined
>(undefined);

const useMultiSelectPanel = () => {
  const context = useContext(MultiSelectPanelContext);
  if (!context) {
    throw new Error(
      "MultiSelect panel parts must be used within a MultiSelectPanel",
    );
  }
  return context;
};

interface MultiSelectPanelProps {
  className?: string;
  align?: "start" | "center" | "end";
  options?: MultiSelectOption[];
  children?: React.ReactNode;
}

const MultiSelectPanel = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitives.Content>,
  MultiSelectPanelProps
>(
  (
    { className, align = "start", options: panelOptions, children, ...props },
    forwardedRef,
  ) => {
    const { isMobile } = useIsMobile();
    const { options, searchTerm, remoteSearch } = useMultiSelect();

    const allOptions = useMemo(
      () => (panelOptions && panelOptions.length > 0 ? panelOptions : options),
      [panelOptions, options],
    );

    const filteredOptions = useMemo(() => {
      if (remoteSearch) return allOptions;
      return allOptions.filter((opt) => matchesSearch(opt.label, searchTerm));
    }, [allOptions, remoteSearch, searchTerm]);

    const panel = useMemo(
      () => ({ allOptions, filteredOptions }),
      [allOptions, filteredOptions],
    );

    return (
      <MultiSelectPanelContext.Provider value={panel}>
        <PopoverContent
          ref={forwardedRef}
          side="bottom"
          sideOffset={8}
          align={align}
          className={cx(
            "max-h-72 min-h-24 w-[calc(var(--radix-popover-trigger-width))] overflow-y-auto",
            "pt-0",
            className,
          )}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            if (isMobile) return;

            const target = e.currentTarget as HTMLElement;
            const searchInput = target?.querySelector("input");
            searchInput?.focus();
          }}
          {...props}
        >
          {children}
        </PopoverContent>
      </MultiSelectPanelContext.Provider>
    );
  },
);

MultiSelectPanel.displayName = "MultiSelectPanel";

const MultiSelectHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="sticky top-0 z-10 bg-gradient-to-b from-background via-background/85 to-transparent pt-4">
    {children}
  </div>
);

const MultiSelectSearch = ({
  placeholder = "Search...",
}: {
  placeholder?: string;
}) => {
  const { searchTerm, setSearchTerm, setOpen } = useMultiSelect();
  return (
    <Input
      type="text"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          setOpen(false);
        }
      }}
      className="w-full rounded-xl bg-background/90 !text-caption shadow-md backdrop-blur-3xl"
      placeholder={placeholder}
      icon={RiSearchLine}
    />
  );
};

const MultiSelectBulkActions = () => {
  const {
    selectedValues,
    selectAll,
    selectNone,
    disabled,
    maxSelections,
    isAtMax,
    offListValues,
  } = useMultiSelect();
  const { allOptions, filteredOptions } = useMultiSelectPanel();

  const allSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((opt) => selectedValues.includes(opt.value));

  if (disabled) return null;

  return (
    <div className="flex w-full justify-end gap-2 py-1">
      <Button
        variant="secondary"
        size="sm"
        onClick={selectAll}
        disabled={
          isAtMax ||
          allSelected ||
          (!!maxSelections &&
            allOptions.length + offListValues.length > maxSelections)
        }
        className="text-nowrap rounded-lg bg-background/40 shadow-lg backdrop-blur-sm"
      >
        Select All
      </Button>
      <Button
        variant="secondary"
        size="sm"
        onClick={selectNone}
        className="text-nowrap rounded-lg bg-background/40 shadow-lg backdrop-blur-sm"
      >
        Select None
      </Button>
    </div>
  );
};

const MultiSelectMatchMode = () => {
  const {
    selectedValues,
    excludeValues,
    enableMatchMode,
    matchMode,
    onMatchModeChange,
  } = useMultiSelect();

  if (!enableMatchMode || selectedValues.length + excludeValues.length < 2) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 px-2 py-1 text-caption text-foreground-secondary">
      <span>Match:</span>
      <button
        type="button"
        className={cx(
          "rounded px-2 py-0.5 font-medium transition-colors",
          matchMode === "any"
            ? "bg-accent text-accent-foreground"
            : "text-foreground-muted hover:text-foreground",
        )}
        onClick={() => onMatchModeChange?.("any")}
      >
        any
      </button>
      <button
        type="button"
        className={cx(
          "rounded px-2 py-0.5 font-medium transition-colors",
          matchMode === "all"
            ? "bg-accent text-accent-foreground"
            : "text-foreground-muted hover:text-foreground",
        )}
        onClick={() => onMatchModeChange?.("all")}
      >
        all
      </button>
    </div>
  );
};

const OptionList = ({
  emptyMessage = "No options found.",
  items,
}: {
  emptyMessage?: string;
  items?: React.ReactNode;
}) => {
  const { searchTerm, remoteSearch } = useMultiSelect();
  const { filteredOptions } = useMultiSelectPanel();
  const itemChildren = React.Children.toArray(items).filter(isMultiSelectItem);
  const hasVisibleRow =
    itemChildren.length > 0
      ? remoteSearch ||
        itemChildren.some((child) =>
          matchesSearch(itemSearchLabel(child.props), searchTerm),
        )
      : filteredOptions.length > 0;

  return (
    <div className="p-1">
      {!hasVisibleRow ? (
        <div className="px-3 py-2 text-body text-muted-foreground">
          {emptyMessage}
        </div>
      ) : itemChildren.length > 0 ? (
        <div className="space-y-1">{itemChildren}</div>
      ) : (
        <div className="space-y-1">
          {filteredOptions.map((option) => (
            <OptionRow
              key={option.value}
              value={option.value}
              label={option.label}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const MultiSelectOptions = ({
  emptyMessage,
  children,
}: {
  emptyMessage?: string;
  children?: React.ReactNode;
}) => <OptionList emptyMessage={emptyMessage} items={children} />;

interface MultiSelectContentProps {
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  align?: "start" | "center" | "end";
  children?: React.ReactNode;
}

const MultiSelectContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitives.Content>,
  MultiSelectContentProps
>(
  (
    {
      searchPlaceholder = "Search...",
      emptyMessage = "No options found.",
      className,
      align = "start",
      children,
      ...props
    },
    forwardedRef,
  ) => {
    const itemsFromChildren = useMemo(
      () =>
        React.Children.toArray(children)
          .filter(isMultiSelectItem)
          .map((child) => ({
            value: child.props.value,
            label: itemSearchLabel(child.props),
          })),
      [children],
    );

    return (
      <MultiSelectPanel
        ref={forwardedRef}
        align={align}
        className={className}
        options={itemsFromChildren}
        {...props}
      >
        <MultiSelectHeader>
          <MultiSelectSearch placeholder={searchPlaceholder} />
          <MultiSelectBulkActions />
          <MultiSelectMatchMode />
        </MultiSelectHeader>
        <OptionList emptyMessage={emptyMessage} items={children} />
      </MultiSelectPanel>
    );
  },
);

MultiSelectContent.displayName = "MultiSelectContent";

interface MultiSelectPillsProps {
  className?: string;
  /** Override each selected item's layout, e.g. aligned rows with accessories. */
  pillClassName?: string;
  tooltipContent?: (value: string) => React.ReactNode;
  /** Extra control inside each pill, before its remove button (e.g. a per-item value). */
  pillAccessory?: (value: string) => React.ReactNode;
}

const MultiSelectPills = ({
  className,
  pillClassName,
  tooltipContent,
  pillAccessory,
}: MultiSelectPillsProps) => {
  const {
    selectedValues,
    excludeValues,
    options,
    removeValue,
    toggleOperator,
    disabled,
    enableOperatorToggle,
    showOffListValues,
  } = useMultiSelect();

  const allDisplayValues = enableOperatorToggle
    ? [...selectedValues, ...excludeValues]
    : selectedValues;

  if (allDisplayValues.length === 0) {
    return null;
  }

  const handleRemove = (value: string, index: number) => {
    if (disabled) return;
    removeValue(value, index < selectedValues.length ? index : undefined);
  };

  return (
    <div className={cx("flex flex-wrap gap-2", className)}>
      {allDisplayValues.map((value, index) => {
        const option = options.find((opt) => opt.value === value);
        if (!option && !(showOffListValues && canLabelItself(value)))
          return null;
        const label = option?.label ?? value;
        const isExcluded = excludeValues.includes(value);

        return (
          <div
            // Free-text values can repeat, so the value alone is not a unique key.
            key={`${index}:${String(value)}`}
            className={cx(
              "flex max-w-full items-center gap-1.5 rounded-md px-2 py-1 text-caption",
              isExcluded
                ? "bg-status-error-bg text-status-error-text"
                : "bg-muted text-foreground",
              pillClassName,
            )}
          >
            {enableOperatorToggle && (
              <button
                type="button"
                onClick={() => !disabled && toggleOperator(value)}
                disabled={disabled}
                className={cx(
                  "rounded px-1 py-0.5 text-tiny font-semibold",
                  isExcluded
                    ? "bg-status-error-text/10 text-status-error-text"
                    : "bg-accent-foreground/10 text-accent-foreground",
                  disabled && "cursor-not-allowed opacity-50",
                )}
              >
                {isExcluded ? "is not" : "is"}
              </button>
            )}
            {tooltipContent ? (
              <Tooltip
                triggerAsChild
                content={tooltipContent(value)}
                side="top"
              >
                <span
                  className={cx(
                    "min-w-0 break-words",
                    isExcluded && "line-through",
                  )}
                >
                  {label}
                </span>
              </Tooltip>
            ) : (
              <span
                className={cx(
                  "min-w-0 break-words",
                  isExcluded && "line-through",
                )}
              >
                {label}
              </span>
            )}
            {pillAccessory?.(value)}
            <button
              type="button"
              onClick={() => handleRemove(value, index)}
              disabled={disabled}
              className={cx(
                "flex shrink-0 items-center justify-center rounded hover:bg-hover",
                disabled && "pointer-events-none cursor-not-allowed opacity-50",
              )}
              aria-label={`Remove ${label}`}
            >
              <RiCloseLine className="size-3.5 text-muted-foreground" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

MultiSelectPills.displayName = "MultiSelectPills";

/** Shared row rendering for option items (used by both OptionList and MultiSelectItem). */
function OptionRow({
  value,
  label,
  className: extraClassName,
}: {
  value: string;
  label: React.ReactNode;
  className?: string;
}) {
  const {
    selectedValues,
    toggleValue,
    disabled,
    isAtMax,
    enableOperatorToggle,
    excludeValues,
    toggleOperator,
  } = useMultiSelect();

  const isExcluded = enableOperatorToggle && excludeValues.includes(value);
  const isIncluded = selectedValues.includes(value);
  const isSelected = isIncluded || isExcluded;
  const isItemDisabled = disabled || (isAtMax && !isSelected);

  return (
    <div
      role="option"
      aria-selected={isSelected}
      className={cx(
        "group/row flex items-center gap-2 rounded px-2 py-1 transition-colors",
        isExcluded
          ? "bg-status-error-bg"
          : isIncluded && enableOperatorToggle
            ? "bg-accent"
            : "",
        isItemDisabled
          ? "cursor-not-allowed opacity-50"
          : "cursor-pointer hover:bg-hover focus-visible:bg-hover",
        extraClassName,
      )}
      onClick={() => !isItemDisabled && toggleValue(value)}
    >
      <Checkbox
        checked={isSelected}
        onCheckedChange={() => !isItemDisabled && toggleValue(value)}
        disabled={isItemDisabled}
        className={cx(
          "pointer-events-none shrink-0",
          isExcluded &&
            "border-status-error-text data-[state=checked]:border-status-error-text data-[state=checked]:bg-status-error-text",
        )}
      />
      <span
        className={cx(
          "min-w-0 flex-1 break-words",
          isExcluded && "text-status-error-text line-through",
          isIncluded &&
            enableOperatorToggle &&
            "font-medium text-accent-foreground",
        )}
      >
        {label}
      </span>
      {enableOperatorToggle && (
        <button
          type="button"
          className={cx(
            "ml-auto shrink-0 rounded px-1.5 py-0.5 text-tiny font-semibold transition-all",
            isExcluded
              ? "border border-status-error-text/20 bg-status-error-bg text-status-error-text"
              : isIncluded
                ? "border border-accent-foreground/20 bg-accent text-accent-foreground"
                : "text-foreground-muted opacity-0 group-hover/row:opacity-40",
          )}
          onClick={(e) => {
            e.stopPropagation();
            if (isSelected) {
              toggleOperator(value);
            }
          }}
        >
          {isExcluded ? "is not" : "is"}
        </button>
      )}
    </div>
  );
}

interface MultiSelectItemProps {
  value: string;
  label?: string;
  children: React.ReactNode;
}

const MultiSelectItem = ({ value, label, children }: MultiSelectItemProps) => {
  const { searchTerm, remoteSearch } = useMultiSelect();

  const isVisible =
    remoteSearch ||
    matchesSearch(itemSearchLabel({ value, label, children }), searchTerm);

  if (!isVisible) return null;

  return (
    <OptionRow
      value={value}
      label={children}
      className="text-foreground sm:text-body"
    />
  );
};

MultiSelectItem.displayName = "MultiSelectItem";

export const MultiSelect = MultiSelectRoot;
MultiSelect.displayName = "MultiSelect";

export {
  MultiSelectBulkActions,
  MultiSelectContent,
  MultiSelectHeader,
  MultiSelectItem,
  MultiSelectMatchMode,
  MultiSelectOptions,
  MultiSelectPanel,
  MultiSelectPills,
  MultiSelectRoot,
  MultiSelectSearch,
  MultiSelectTrigger,
};
